import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ComposedChart, Bar, Line,
} from 'recharts';
import { CText } from '../../components/common';
import { getAllCompanyNames, getCompanyDetails, getCompanyRatiosHistory, CompanyRatioPoint } from '../../api/copilot';

// ── Constants ──

const RATIO_METRIC_MAP = [
  { key: 'pe' as const, label: 'P/E', barMatches: ['eps in rs', 'eps', 'earnings per share'], barLabel: 'EPS (Rs)', source: 'yearlyResults' as const },
  { key: 'pb' as const, label: 'P/B', barMatches: ['book value'], barLabel: 'Book Value (Rs)', source: 'balanceSheets' as const },
  { key: 'mcapSales' as const, label: 'MCap/Sales', barMatches: ['sales'], barLabel: 'Sales (Cr)', source: 'yearlyResults' as const },
  { key: 'evEbitda' as const, label: 'EV/EBITDA', barMatches: ['operating profit'], barLabel: 'EBITDA (Cr)', source: 'yearlyResults' as const },
];

function getCompanyStyle(index: number) {
  if (index === 0) return { color: '#6366f1', dash: '', width: 3 }; // primary: always indigo solid
  const hue = (30 + (index - 1) * 137.5) % 360; // golden-angle spacing for max distinction
  const dashPatterns = ['8 4', '4 4', '12 4 2 4', '2 2', ''];
  return {
    color: `hsl(${hue}, 75%, 55%)`,
    dash: dashPatterns[(index - 1) % dashPatterns.length],
    width: 2.5,
  };
}

// ── Types ──

interface ComparisonCompany {
  name: string;
  tickerSymbol: string | null;
  ratiosData: CompanyRatioPoint[] | null;
  loading: boolean;
  error: string | null;
}

interface ValuationMetricsProps {
  primaryName: string;
  primaryTicker: string;
  ratiosHistory: CompanyRatioPoint[];
  yearlyResults?: Array<Record<string, any>>;
  quarterlyResults?: Array<Record<string, any>>;
  balanceSheets?: Array<Record<string, any>>;
  currentRatios?: { pe?: number; pb?: number; mcapSales?: number; evEbitda?: number };
  peerNames?: string[];
  colors: any;
}

// ── Helpers ──

function findMetricRow(
  barMatches: readonly string[],
  ...tables: (Array<Record<string, any>> | undefined)[]
): Record<string, any> | undefined {
  for (const table of tables) {
    if (!table || table.length === 0) continue;
    const labelKey = Object.keys(table[0])[0];
    const found = table.find(row => {
      const name = String(row[labelKey] || '').toLowerCase();
      return barMatches.some(m => name.includes(m));
    });
    if (found) return found;
  }
  return undefined;
}

function extractTimeSeries(row: Record<string, any>): { period: string; value: number }[] {
  const labelKey = Object.keys(row)[0];
  const data: { period: string; value: number }[] = [];
  for (const [key, raw] of Object.entries(row)) {
    if (key === labelKey || key === 'TTM') continue;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''));
    if (!isNaN(num)) data.push({ period: key, value: num });
  }
  return data;
}

/** Normalize a ratio date like "2023-03-31" → "Mar 2023" to match financial period labels */
function normalizeToYearMonth(dateStr: string): string {
  // Parse "YYYY-MM-DD" directly to avoid timezone issues with new Date()
  const match = dateStr.match(/^(\d{4})-(\d{2})/);
  if (match) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(match[2], 10) - 1]} ${match[1]}`;
  }
  // Fallback for other formats
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getMonth()]} ${d.getFullYear()}`;
}

// ── Component ──

export const ValuationMetrics: React.FC<ValuationMetricsProps> = ({
  primaryName, primaryTicker, ratiosHistory,
  yearlyResults, quarterlyResults, balanceSheets, currentRatios, peerNames, colors,
}) => {
  const hasRatiosHistory = ratiosHistory && ratiosHistory.length > 0;

  const hasFinancialTrends = RATIO_METRIC_MAP.some(opt => {
    const tables = opt.source === 'balanceSheets' ? [balanceSheets] : [quarterlyResults, yearlyResults];
    return !!findMetricRow(opt.barMatches, ...tables);
  });

  // Single active ratio (default P/E)
  const [activeRatioKey, setActiveRatioKey] = useState<string>('pe');

  // Comparison state
  const [comparisonCompanies, setComparisonCompanies] = useState<ComparisonCompany[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [allCompanyNames, setAllCompanyNames] = useState<string[]>([]);
  const [namesLoading, setNamesLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lazy-load company names on first open
  const handleOpenSearch = useCallback(async () => {
    if (comparisonCompanies.length >= 10) return;
    setSearchOpen(true);
    setSearchQuery('');
    if (allCompanyNames.length === 0 && !namesLoading) {
      setNamesLoading(true);
      try {
        const names = await getAllCompanyNames();
        setAllCompanyNames(names);
      } catch { /* ignore */ }
      setNamesLoading(false);
    }
  }, [allCompanyNames.length, namesLoading, comparisonCompanies.length]);

  // Add comparison company
  const addComparison = useCallback(async (name: string) => {
    setSearchOpen(false);
    setSearchQuery('');
    const entry: ComparisonCompany = { name, tickerSymbol: null, ratiosData: null, loading: true, error: null };
    setComparisonCompanies(prev => [...prev, entry]);
    try {
      const details = await getCompanyDetails(name);
      const ticker = details.data?.ticker_symbol;
      if (!ticker) {
        setComparisonCompanies(prev =>
          prev.map(c => c.name === name ? { ...c, loading: false, error: 'No ticker found' } : c)
        );
        return;
      }
      const ratios = await getCompanyRatiosHistory(ticker);
      setComparisonCompanies(prev =>
        prev.map(c => c.name === name ? { ...c, tickerSymbol: ticker, ratiosData: ratios, loading: false } : c)
      );
    } catch {
      setComparisonCompanies(prev =>
        prev.map(c => c.name === name ? { ...c, loading: false, error: 'Failed to load' } : c)
      );
    }
  }, []);

  const removeComparison = useCallback((name: string) => {
    setComparisonCompanies(prev => prev.filter(c => c.name !== name));
  }, []);

  // Filter dropdown names
  const alreadySelected = new Set([primaryName, ...comparisonCompanies.map(c => c.name)]);
  const filteredNames = allCompanyNames.filter(n =>
    !alreadySelected.has(n) && n.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── Ratio badges (always visible) ──
  const ratioBadges = currentRatios ? [
    { label: 'P/E', value: currentRatios.pe },
    { label: 'P/B', value: currentRatios.pb },
    { label: 'MCap/Sales', value: currentRatios.mcapSales },
    { label: 'EV/EBITDA', value: currentRatios.evEbitda },
  ].filter(b => b.value != null && !isNaN(b.value!)) : [];

  // ── Nothing to show ──
  if (!hasRatiosHistory && !hasFinancialTrends && ratioBadges.length === 0) return null;

  // ── Active ratio config ──
  const activeConfig = RATIO_METRIC_MAP.find(r => r.key === activeRatioKey) || RATIO_METRIC_MAP[0];

  // ── Build financial metric bar data ──
  const metricTables = activeConfig.source === 'balanceSheets' ? [balanceSheets] : [quarterlyResults, yearlyResults];
  const metricRow = findMetricRow(activeConfig.barMatches, ...metricTables);
  const financialData = metricRow ? extractTimeSeries(metricRow) : [];

  // Build a lookup from normalized period → bar value
  const barLookup = new Map<string, number>();
  financialData.forEach(d => barLookup.set(d.period, d.value));

  // ── Build ratio line data ──
  // Map ratio dates to normalized year-month for alignment with financial data
  const ratioLookup = new Map<string, number>();
  ratiosHistory.forEach(p => {
    if (p[activeConfig.key] != null) {
      ratioLookup.set(normalizeToYearMonth(p.date), p[activeConfig.key] as number);
    }
  });

  // Comparison lookups: compIdx → Map<normalizedDate, value>
  const compLookups: Map<string, number>[] = comparisonCompanies.map(comp => {
    const m = new Map<string, number>();
    comp.ratiosData?.forEach(p => {
      if (p[activeConfig.key] != null) {
        m.set(normalizeToYearMonth(p.date), p[activeConfig.key] as number);
      }
    });
    return m;
  });

  // ── Build combined chart data ──
  const buildCombinedChartData = () => {
    // Collect all unique periods from both bar data and ratio data
    const allPeriods = new Set<string>();
    financialData.forEach(d => allPeriods.add(d.period));
    ratiosHistory.forEach(p => allPeriods.add(normalizeToYearMonth(p.date)));
    comparisonCompanies.forEach(comp => {
      comp.ratiosData?.forEach(p => allPeriods.add(normalizeToYearMonth(p.date)));
    });

    // Sort periods chronologically
    const sorted = Array.from(allPeriods).sort((a, b) => {
      const parse = (s: string) => {
        const months: Record<string, number> = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
        const parts = s.split(' ');
        const mon = months[parts[0]] ?? 0;
        const yr = parseInt(parts[1]?.replace("'", '')) || 0;
        // Handle 2-digit years from formatDateLabel ("Mar '23") and 4-digit from normalizeToYearMonth ("Mar 2023")
        const fullYear = yr < 100 ? 2000 + yr : yr;
        return fullYear * 12 + mon;
      };
      return parse(a) - parse(b);
    });

    return sorted.map(period => {
      const entry: Record<string, any> = { period };
      // Always set every dataKey — use null for missing values so
      // Recharts connectNulls can bridge gaps between sparse data points.
      // (Bar and ratio periods rarely overlap, so without explicit nulls
      // the keys are absent → undefined → connectNulls won't bridge them.)
      entry.barValue = barLookup.get(period) ?? null;
      entry.ratioValue = ratioLookup.get(period) ?? null;
      compLookups.forEach((lookup, i) => {
        entry[`comp_${i}`] = lookup.get(period) ?? null;
      });
      return entry;
    });
  };

  const chartData = buildCombinedChartData();
  const hasBars = chartData.some(d => d.barValue != null);
  const hasLines = chartData.some(d => d.ratioValue != null);

  // ── Combined Tooltip ──
  const CombinedTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div
        className="p-3 rounded-lg shadow-lg"
        style={{
          backgroundColor: colors.inputBackground,
          border: `1px solid ${colors.borderColor}`,
          fontSize: 12,
        }}
      >
        <div style={{ color: colors.grayScale400, marginBottom: 6, fontWeight: 500 }}>{label}</div>
        {payload.map((p: any) => {
          if (p.dataKey === 'barValue') {
            return (
              <div key="bar" className="flex justify-between gap-4 mb-1">
                <span style={{ color: colors.grayScale500 }}>{activeConfig.barLabel}:</span>
                <span style={{ color: colors.textColor, fontWeight: 600 }}>
                  {p.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </span>
              </div>
            );
          }
          if (p.dataKey === 'ratioValue') {
            return (
              <div key="primary" className="flex items-center gap-1.5 mb-1">
                <svg width="16" height="8"><line x1="0" y1="4" x2="16" y2="4" stroke={getCompanyStyle(0).color} strokeWidth={2} /></svg>
                <span style={{ color: colors.grayScale500 }}>{primaryName}:</span>
                <span style={{ color: colors.textColor, fontWeight: 600 }}>{p.value.toFixed(2)}</span>
              </div>
            );
          }
          const match = p.dataKey.match(/^comp_(\d+)$/);
          if (match) {
            const idx = parseInt(match[1]);
            const style = getCompanyStyle(idx + 1);
            const comp = comparisonCompanies[idx];
            return (
              <div key={p.dataKey} className="flex items-center gap-1.5 mb-1">
                <svg width="16" height="8"><line x1="0" y1="4" x2="16" y2="4" stroke={style.color} strokeWidth={2} strokeDasharray={style.dash} /></svg>
                <span style={{ color: colors.grayScale500 }}>{comp?.name}:</span>
                <span style={{ color: colors.textColor, fontWeight: 600 }}>{p.value.toFixed(2)}</span>
              </div>
            );
          }
          return null;
        })}
      </div>
    );
  };

  // ── Render ──

  return (
    <div
      className="p-4 rounded-xl mb-4"
      style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}` }}
    >
      <CText type="M14" color={colors.grayScale500} style={{ marginBottom: 12 }}>Valuation & Metrics</CText>

      {/* Current ratio badges */}
      {ratioBadges.length > 0 && (
        <div className="flex gap-3 mb-4 flex-wrap">
          {ratioBadges.map(b => (
            <div
              key={b.label}
              className="px-3 py-1.5 rounded-lg"
              style={{ backgroundColor: colors.primary + '12', border: `1px solid ${colors.primary}30` }}
            >
              <CText type="r10" color={colors.grayScale500}>{b.label}</CText>
              <CText type="M14" color={colors.primary} style={{ display: 'block' }}>
                {b.value!.toFixed(1)}
              </CText>
            </div>
          ))}
        </div>
      )}

      {/* Ratio toggle buttons (single selection) */}
      {(hasRatiosHistory || hasFinancialTrends) && (
        <div className="flex gap-2 mb-3 flex-wrap">
          {RATIO_METRIC_MAP.map(opt => (
            <button
              key={opt.key}
              onClick={() => setActiveRatioKey(opt.key)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                backgroundColor: activeRatioKey === opt.key ? '#6366f1' : colors.backgroundColor,
                color: activeRatioKey === opt.key ? '#fff' : colors.grayScale500,
                border: `1px solid ${activeRatioKey === opt.key ? '#6366f1' : colors.borderColor}`,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Peer suggestion chips */}
      {(hasRatiosHistory || hasFinancialTrends) && (() => {
        const availablePeers = (peerNames || []).filter(
          name => name !== primaryName && !comparisonCompanies.some(c => c.name === name)
        ).slice(0, Math.max(0, 10 - comparisonCompanies.length));
        if (availablePeers.length === 0) return null;
        return (
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <CText type="r12" color={colors.grayScale400} style={{ marginRight: 2 }}>Peers:</CText>
            {availablePeers.map(name => (
              <button
                key={name}
                onClick={() => addComparison(name)}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-colors hover:opacity-80"
                style={{
                  backgroundColor: 'transparent',
                  color: colors.primary,
                  border: `1px solid ${colors.primary}40`,
                }}
              >
                {name}
                <span style={{ fontSize: 14, lineHeight: 1 }}>+</span>
              </button>
            ))}
          </div>
        );
      })()}

      {/* Comparison bar */}
      {(hasRatiosHistory || hasFinancialTrends) && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          {comparisonCompanies.map((comp, i) => {
            const style = getCompanyStyle(i + 1);
            return (
              <span
                key={comp.name}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs"
                style={{
                  backgroundColor: style.color + '20',
                  color: style.color,
                  border: `1px solid ${style.color}40`,
                }}
              >
                {comp.loading && (
                  <span className="inline-block w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: style.color }} />
                )}
                {comp.error && <span title={comp.error}>!</span>}
                {comp.name}
                <button onClick={() => removeComparison(comp.name)} className="hover:opacity-70 ml-0.5">
                  &times;
                </button>
              </span>
            );
          })}

          {comparisonCompanies.length < 10 && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={handleOpenSearch}
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs transition-colors"
                style={{
                  backgroundColor: colors.backgroundColor,
                  color: colors.grayScale500,
                  border: `1px dashed ${colors.borderColor}`,
                }}
              >
                + Compare
              </button>

              {searchOpen && (
                <div
                  className="absolute top-full mt-1 left-0 w-64 max-h-64 overflow-hidden rounded-lg shadow-lg z-50"
                  style={{
                    backgroundColor: colors.backgroundColor,
                    border: `1px solid ${colors.borderColor}`,
                  }}
                >
                  <div className="p-2" style={{ borderBottom: `1px solid ${colors.borderColor}` }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      placeholder="Search companies..."
                      className="w-full bg-transparent text-sm outline-none px-2 py-1"
                      style={{ color: colors.textColor }}
                      autoFocus
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {namesLoading ? (
                      <div className="p-3 text-center">
                        <CText type="r12" color={colors.grayScale400}>Loading...</CText>
                      </div>
                    ) : filteredNames.length === 0 && !(peerNames || []).some(n => !alreadySelected.has(n)) ? (
                      <div className="p-3 text-center">
                        <CText type="r12" color={colors.grayScale400}>
                          {searchQuery ? 'No matches' : 'No companies available'}
                        </CText>
                      </div>
                    ) : (
                      <>
                        {!searchQuery && (() => {
                          const suggestedPeers = (peerNames || []).filter(n => !alreadySelected.has(n));
                          if (suggestedPeers.length === 0) return null;
                          return (
                            <>
                              <div className="px-3 pt-2 pb-1">
                                <CText type="r10" color={colors.grayScale400}>Suggested peers</CText>
                              </div>
                              {suggestedPeers.map(name => (
                                <div
                                  key={name}
                                  className="px-3 py-2 cursor-pointer hover:opacity-80 text-sm flex items-center gap-2"
                                  style={{ color: colors.primary }}
                                  onClick={() => addComparison(name)}
                                >
                                  <span>{name}</span>
                                  <span style={{ fontSize: 11, color: colors.grayScale400 }}>peer</span>
                                </div>
                              ))}
                              {filteredNames.length > 0 && (
                                <div className="mx-3 my-1" style={{ borderBottom: `1px solid ${colors.borderColor}` }} />
                              )}
                            </>
                          );
                        })()}
                        {filteredNames.slice(0, 50).map(name => (
                          <div
                            key={name}
                            className="px-3 py-2 cursor-pointer hover:opacity-80 text-sm"
                            style={{ color: colors.textColor }}
                            onClick={() => addComparison(name)}
                          >
                            {name}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ Combined Dual-Axis Chart ═══ */}
      {(hasBars || hasLines) ? (
        <>
          <div style={{ height: 300, width: '100%' }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.borderColor} />
                <XAxis
                  dataKey="period"
                  tick={{ fontSize: 10, fill: colors.grayScale400 }}
                  interval="preserveStartEnd"
                  angle={-30}
                  textAnchor="end"
                  height={50}
                />
                {/* Left Y-axis: financial metric bars */}
                {hasBars && (
                  <YAxis
                    yAxisId="left"
                    tick={{ fontSize: 10, fill: colors.grayScale400 }}
                    width={60}
                    tickFormatter={(v: number) =>
                      Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}K` : v.toLocaleString()
                    }
                  />
                )}
                {/* Right Y-axis: ratio lines */}
                {hasLines && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 10, fill: colors.grayScale400 }}
                    width={45}
                  />
                )}
                <Tooltip content={<CombinedTooltip />} />

                {/* Financial metric bars (primary company only) */}
                {hasBars && (
                  <Bar
                    yAxisId="left"
                    dataKey="barValue"
                    fill={colors.primary + '30'}
                    stroke={colors.primary}
                    strokeWidth={1}
                    radius={[4, 4, 0, 0]}
                    name={activeConfig.barLabel}
                  />
                )}

                {/* Primary company ratio line */}
                {hasLines && (
                  <Line
                    yAxisId={hasLines ? 'right' : 'left'}
                    type="monotone"
                    dataKey="ratioValue"
                    stroke={getCompanyStyle(0).color}
                    strokeWidth={getCompanyStyle(0).width}
                    dot={false}
                    activeDot={{ r: 4 }}
                    connectNulls
                    name={primaryName}
                  />
                )}

                {/* Comparison company ratio lines */}
                {comparisonCompanies.map((comp, i) => {
                  if (!comp.ratiosData || comp.ratiosData.length === 0) return null;
                  const style = getCompanyStyle(i + 1);
                  return (
                    <Line
                      key={`comp_${i}`}
                      yAxisId={hasLines ? 'right' : 'left'}
                      type="monotone"
                      dataKey={`comp_${i}`}
                      stroke={style.color}
                      strokeWidth={style.width}
                      strokeDasharray={style.dash}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls
                      name={comp.name}
                    />
                  );
                })}
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex gap-4 mt-2 flex-wrap justify-center">
            {/* Bar legend entry */}
            {hasBars && (
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: colors.primary + '30', border: `1px solid ${colors.primary}` }} />
                <CText type="r10" color={colors.grayScale500}>{activeConfig.barLabel}</CText>
              </div>
            )}
            {/* Primary line */}
            {hasLines && (
              <div className="flex items-center gap-1.5">
                <svg width="20" height="8">
                  <line x1="0" y1="4" x2="20" y2="4" stroke={getCompanyStyle(0).color} strokeWidth={getCompanyStyle(0).width} />
                </svg>
                <CText type="r10" color={colors.grayScale500}>{primaryName}</CText>
              </div>
            )}
            {/* Comparison lines */}
            {comparisonCompanies.map((comp, i) => {
              if (!comp.ratiosData || comp.ratiosData.length === 0) return null;
              const style = getCompanyStyle(i + 1);
              return (
                <div key={comp.name} className="flex items-center gap-1.5">
                  <svg width="20" height="8">
                    <line x1="0" y1="4" x2="20" y2="4" stroke={style.color} strokeWidth={style.width} strokeDasharray={style.dash} />
                  </svg>
                  <CText type="r10" color={colors.grayScale500}>{comp.name}</CText>
                </div>
              );
            })}
          </div>

          {/* Note when bars render but ratio line is missing */}
          {hasBars && !hasLines && !hasRatiosHistory && (
            <div className="text-center mt-2">
              <CText type="r10" color={colors.grayScale400}>
                Historical {activeConfig.label} ratio data unavailable
              </CText>
            </div>
          )}
        </>
      ) : (hasRatiosHistory || hasFinancialTrends) ? (
        <div className="text-center py-6">
          <CText type="r12" color={colors.grayScale400}>No chart data available for {activeConfig.label}</CText>
        </div>
      ) : null}
    </div>
  );
};
